import { useDialog } from 'muibox';
import moment from 'moment';
import { useContext } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import { SettingsContext } from '../store/SettingsContext';
import { UserContext } from '../store/UserContext';
import { OnlyFansPostStory } from '../types/onlyFansTypes';
import { handleHttpError } from '../utils/common';
import useAxios from './useAxios';

export interface AutoStorySchedule {
    id: string;
    onlyfansId: number;
    name: string;
    mode: 'MANUAL' | 'ALBUM';
    active: boolean;
    colorId: string | null;
    startDate: string;
    repeatAmount: number | null;
    repeatUnit: 'HOURS' | 'DAYS' | null;
    nextPostDate: string;
    lastPostDate: string | null;
    deleteAfterHours: number | null;
    albumId: number | null;
    postImage: boolean;
    postVideo: boolean;
    postGif: boolean;
    captionCategory: string | null;
    vaultMediaId: number | null;
    canvasWidth: number | null;
    canvasHeight: number | null;
    crop: { x: number; y: number; width: number; height: number } | null;
    texts: Record<string, OnlyFansPostStory.TextItem> | null;
    createdAt: string;
    updatedAt: string;
}

export interface AutoStoryLog {
    id: string;
    scheduleId: string;
    storyId: number | null;
    mediaId: number | null;
    sourceMediaId: number | null;
    caption: string | null;
    postedAt: string;
    deleteAfterDate: string | null;
    statsOnly: boolean;
    deletedAt: string | null;
    stats: any;
    status: 'posted' | 'deleted' | 'failed';
    error: string | null;
}

/** Request body accepted by POST/PUT /api/autoStories/:userId/(:scheduleId) - Joi rejects unknown keys, so this must stay in sync. */
export interface AutoStorySchedulePayload {
    name: string;
    mode: 'manual' | 'album';
    colorId: string | null;
    startDate: string;
    repeatAmount: number | null;
    repeatUnit: 'hours' | 'days' | null;
    deleteAfterHours: number | null;
    active: boolean;
    albumId: number | null;
    postImage: boolean;
    postVideo: boolean;
    postGif: boolean;
    captionCategory: string | null;
    vaultMediaId: number | null;
    canvasWidth: number | null;
    canvasHeight: number | null;
    crop: { x: number; y: number; width: number; height: number } | null;
    texts: Record<string, OnlyFansPostStory.TextItem> | null;
}

const useSextforceAutoStories = () => {
    const userContext = useContext(UserContext);
    const settingsContext = useContext(SettingsContext);
    const axios = useAxios();
    const dialog = useDialog();
    const params = useParams();
    const queryClient = useQueryClient();

    // Fetch the Auto Story schedules for this Subscriber
    const fetchSchedules = async (): Promise<AutoStorySchedule[]> => {
        if (!userContext.jwtToken || !settingsContext.apiKey || !params.userId) {
            return [];
        }

        const query: string = `${settingsContext.routes.autoStories.base}${params.userId}`;

        const data = await axios
            .get(query)
            .then(async response => response.data as AutoStorySchedule[])
            .catch(error => {
                handleHttpError(error, dialog);

                return [];
            });

        return data;
    };

    const schedulesQuery = useQuery(['autoStories', params.userId, userContext.jwtToken], () => fetchSchedules(), {
        refetchOnWindowFocus: false,
        staleTime: 1000 * 60 * 5,
        enabled: userContext.jwtToken && params.userId && settingsContext.apiKey ? true : false,
    });

    const createSchedule = async (payload: AutoStorySchedulePayload): Promise<AutoStorySchedule | null> => {
        if (!userContext.jwtToken || !settingsContext.apiKey || !params.userId) {
            return null;
        }

        const query: string = `${settingsContext.routes.autoStories.base}${params.userId}/`;

        const data = await axios
            .post(query, payload)
            .then(response => {
                if (response.status === 200) {
                    queryClient.invalidateQueries('autoStories');
                    queryClient.invalidateQueries('autoStoriesLogs');

                    return response.data as AutoStorySchedule;
                } else {
                    return Promise.reject(response.data);
                }
            })
            .catch(error => {
                handleHttpError(error, dialog);

                return null;
            });

        return data;
    };

    const updateSchedule = async (scheduleId: string, payload: AutoStorySchedulePayload): Promise<AutoStorySchedule | null> => {
        if (!userContext.jwtToken || !settingsContext.apiKey || !params.userId) {
            return null;
        }

        const query: string = `${settingsContext.routes.autoStories.base}${params.userId}/${scheduleId}`;

        const data = await axios
            .put(query, payload)
            .then(response => {
                if (response.status === 200) {
                    queryClient.invalidateQueries('autoStories');
                    queryClient.invalidateQueries('autoStoriesLogs');

                    return response.data as AutoStorySchedule;
                } else {
                    return Promise.reject(response.data);
                }
            })
            .catch(error => {
                handleHttpError(error, dialog);

                return null;
            });

        return data;
    };

    const deleteSchedule = async (scheduleId: string): Promise<{ success: boolean }> => {
        if (!userContext.jwtToken || !settingsContext.apiKey || !params.userId) {
            return { success: false };
        }

        const query: string = `${settingsContext.routes.autoStories.base}${params.userId}/${scheduleId}`;

        const data = await axios
            .delete(query)
            .then(response => {
                if (response.status === 200) {
                    queryClient.invalidateQueries('autoStories');
                    queryClient.invalidateQueries('autoStoriesLogs');

                    return response.data;
                } else {
                    return Promise.reject(response.data);
                }
            })
            .catch(error => {
                handleHttpError(error, dialog);

                return { success: false };
            });

        return data;
    };

    const useLogs = (startDate: moment.Moment, endDate: moment.Moment, scheduleId?: string) => {
        const fetchLogs = async (): Promise<AutoStoryLog[]> => {
            if (!userContext.jwtToken || !settingsContext.apiKey || !params.userId) {
                return [];
            }

            const query: string = `${settingsContext.routes.autoStories.base}${params.userId}/logs?${new URLSearchParams({
                startDate: startDate.toISOString(),
                endDate: endDate.toISOString(),
                ...(scheduleId ? { scheduleId } : {}),
            })}`;

            const data = await axios
                .get(query)
                .then(async response => response.data as AutoStoryLog[])
                .catch(error => {
                    handleHttpError(error, dialog);

                    return [];
                });

            return data;
        };

        return useQuery(
            ['autoStoriesLogs', params.userId, startDate.toISOString(), endDate.toISOString(), scheduleId],
            () => fetchLogs(),
            {
                refetchOnWindowFocus: false,
                staleTime: 1000 * 60,
                enabled: userContext.jwtToken && params.userId && settingsContext.apiKey ? true : false,
            },
        );
    };

    return {
        schedules: schedulesQuery.data,
        isLoadingSchedules: schedulesQuery.isLoading,
        createSchedule,
        updateSchedule,
        deleteSchedule,
        useLogs,
    };
};

export default useSextforceAutoStories;
